import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import NoRecord from "../../../Components/NoRecord";
import { showToast } from "../../../utils/handleToast";
import {
  getVoicebotCallTypesMeta,
  getVoicebotCalls,
  getVoicebotCallDetail,
  getVoicebotCallRecording,
} from "../../../API/voicebotApis";

const DISPOSITION_OPTIONS = [
  { value: "", label: "All dispositions" },
  { value: "pending", label: "Pending (not yet called / in retry)" },
];

const formatDate = (value) => {
  if (!value) return "-";
  try {
    return new Date(value).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return value;
  }
};

export default function VoicebotCalls() {
  const [meta, setMeta] = useState(null);
  const [metaLoading, setMetaLoading] = useState(true);
  const [callType, setCallType] = useState("");
  const [segment, setSegment] = useState("");
  const [client, setClient] = useState("");
  const [disposition, setDisposition] = useState("");
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [selectedLoading, setSelectedLoading] = useState(false);
  const [recordingUrl, setRecordingUrl] = useState(null);
  const [recordingLoading, setRecordingLoading] = useState(false);
  const [recordingError, setRecordingError] = useState(false);

  useEffect(() => {
    const fetchMeta = async () => {
      setMetaLoading(true);
      const response = await getVoicebotCallTypesMeta();
      if (response?.success) {
        setMeta(response.data);
        const firstType = Object.keys(response.data)[0];
        if (firstType) setCallType(firstType);
      } else {
        showToast({ variant: "danger", message: "Failed to load voicebot call types" });
      }
      setMetaLoading(false);
    };
    fetchMeta();
  }, []);

  // Reset segment/client/disposition/search when switching call type
  useEffect(() => {
    setSegment("");
    setClient("");
    setDisposition("");
    setSearch("");
  }, [callType]);

  const fetchRows = useCallback(async () => {
    if (!callType) return;
    setLoading(true);
    const params = { call_type: callType };
    if (segment) params.segment = segment;
    if (client) params.client = client;
    if (disposition) params.disposition = disposition;
    if (search) params.search = search;

    const response = await getVoicebotCalls(params);
    if (response?.success && Array.isArray(response.data)) {
      setRows(response.data);
    } else {
      showToast({ variant: "danger", message: "Failed to load calling results" });
      setRows([]);
    }
    setLoading(false);
  }, [callType, segment, client, disposition, search]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const activeMeta = meta && callType ? meta[callType] : null;
  const dispositionOptions = useMemo(() => {
    const seen = new Set();
    rows.forEach((r) => r.disposition && seen.add(r.disposition));
    return [...DISPOSITION_OPTIONS, ...[...seen].sort().map((d) => ({ value: d, label: d }))];
  }, [rows]);

  const handleViewDetail = async (row) => {
    setSelected({ ...row });
    setSelectedLoading(true);
    setRecordingUrl(null);
    setRecordingError(false);
    const response = await getVoicebotCallDetail(row.call_type, row.id);
    if (response?.success) {
      setSelected(response.data);
    } else {
      showToast({ variant: "danger", message: "Failed to load call detail" });
    }
    setSelectedLoading(false);
  };

  const handleCloseDetail = () => {
    if (recordingUrl) URL.revokeObjectURL(recordingUrl);
    setSelected(null);
    setRecordingUrl(null);
    setRecordingError(false);
  };

  const handleLoadRecording = async () => {
    if (!selected) return;
    setRecordingLoading(true);
    setRecordingError(false);
    const response = await getVoicebotCallRecording(selected.call_type, selected.id);
    if (response?.success) {
      setRecordingUrl(URL.createObjectURL(response.data));
    } else {
      setRecordingError(true);
      showToast({ variant: "danger", message: "Recording not available for this call" });
    }
    setRecordingLoading(false);
  };

  const extraColumns = {
    bank_sales: [
      { key: "bank_name", label: "Bank" },
      { key: "branch_name", label: "Branch" },
    ],
    legal_interview: [{ key: "segment", label: "Position" }],
    screening: [
      { key: "segment", label: "Campaign" },
      { key: "result", label: "Result" },
    ],
  }[callType] || [];

  return (
    <>
      <div className="bg-white border-bottom px-3 py-3">
        <div className="row w-100 g-2 align-items-center">
          <div className="col-12 col-lg-2">
            <label className="form-label small fw-semibold mb-1">Call Type</label>
            <select className="form-select" value={callType} onChange={(e) => setCallType(e.target.value)} disabled={metaLoading}>
              {meta &&
                Object.entries(meta).map(([key, info]) => (
                  <option key={key} value={key}>
                    {info.label} ({info.total})
                  </option>
                ))}
            </select>
          </div>

          <div className="col-12 col-lg-2">
            <label className="form-label small fw-semibold mb-1">{activeMeta?.segment_label || "Segment"}</label>
            <select className="form-select" value={segment} onChange={(e) => setSegment(e.target.value)}>
              <option value="">All</option>
              {activeMeta?.segments?.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>

          <div className="col-12 col-lg-2">
            <label className="form-label small fw-semibold mb-1">{activeMeta?.client_label || "Client"}</label>
            <select
              className="form-select"
              value={client}
              onChange={(e) => setClient(e.target.value)}
              disabled={!activeMeta?.client_label}
            >
              <option value="">All</option>
              {activeMeta?.clients?.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>

          <div className="col-12 col-lg-2">
            <label className="form-label small fw-semibold mb-1">Disposition</label>
            <select className="form-select" value={disposition} onChange={(e) => setDisposition(e.target.value)}>
              {dispositionOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          <div className="col-12 col-lg-4">
            <label className="form-label small fw-semibold mb-1">Search (name / mobile)</label>
            <input
              type="text"
              className="form-control"
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="p-3">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 className="mb-0">{activeMeta?.label || "Voicebot"} Calling Results</h4>
          <span className="text-muted">{rows.length} record(s)</span>
        </div>

        {loading ? (
          <div className="d-flex justify-content-center">
            <div className="spinner-border text-primary" role="status" aria-hidden="true" />
          </div>
        ) : rows.length === 0 ? (
          <NoRecord />
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table className="table table-bordered border table-sm" style={{ width: "100%" }}>
              <thead className="thead-light">
                <tr>
                  <th className="p-3 text-left align-top">Name</th>
                  <th className="p-3 text-left align-top">Mobile</th>
                  {extraColumns.map((c) => (
                    <th key={c.key} className="p-3 text-left align-top">
                      {c.label}
                    </th>
                  ))}
                  <th className="p-3 text-left align-top">Disposition</th>
                  <th className="p-3 text-left align-top">Attempts</th>
                  <th className="p-3 text-left align-top">Duration (s)</th>
                  <th className="p-3 text-left align-top">Last Attempt</th>
                  <th className="p-3 text-center align-top">Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => (
                  <tr key={row.id} style={{ background: index % 2 === 0 ? "transparent" : "#fbfbfb" }}>
                    <td className="p-3 text-left">{row.name || "-"}</td>
                    <td className="p-3 text-left">{row.mobile || "-"}</td>
                    {extraColumns.map((c) => (
                      <td key={c.key} className="p-3 text-left">
                        {row[c.key] || "-"}
                      </td>
                    ))}
                    <td className="p-3 text-left">
                      {row.disposition ? (
                        <span className="badge bg-secondary">{row.disposition}</span>
                      ) : (
                        <span className="badge bg-light text-dark">Pending</span>
                      )}
                    </td>
                    <td className="p-3 text-left">{row.attempts}</td>
                    <td className="p-3 text-left">{row.duration_sec}</td>
                    <td className="p-3 text-left">{formatDate(row.last_attempt_at)}</td>
                    <td className="p-3 text-center">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => handleViewDetail(row)}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal show={!!selected} onHide={handleCloseDetail} size="lg" scrollable>
        <Modal.Header closeButton className="border-bottom bg-light">
          <Modal.Title className="fw-bold text-dark">
            {selected?.name} — {selected?.mobile}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          {selectedLoading ? (
            <div className="d-flex justify-content-center py-5">
              <div className="spinner-border text-primary" role="status" aria-hidden="true" />
            </div>
          ) : (
            <>
              <div className="mb-3">
                <strong>Disposition:</strong> {selected?.disposition || "Pending"} &nbsp;|&nbsp;
                <strong>Attempts:</strong> {selected?.attempts} &nbsp;|&nbsp;
                <strong>Duration:</strong> {selected?.duration_sec}s
              </div>

              <div className="mb-3">
                <h6 className="fw-bold">Call Recording</h6>
                {recordingUrl ? (
                  <audio controls src={recordingUrl} style={{ width: "100%" }} />
                ) : (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary"
                    onClick={handleLoadRecording}
                    disabled={recordingLoading || !selected?.attempts}
                  >
                    {recordingLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                        Loading...
                      </>
                    ) : recordingError ? (
                      "Retry Loading Recording"
                    ) : (
                      "Load Recording"
                    )}
                  </button>
                )}
              </div>

              {selected?.captured_data && Object.keys(selected.captured_data).length > 0 && (
                <div className="mb-3">
                  <h6 className="fw-bold">Captured Data</h6>
                  <table className="table table-sm table-bordered">
                    <tbody>
                      {Object.entries(selected.captured_data).map(([k, v]) => (
                        <tr key={k}>
                          <td className="fw-semibold" style={{ width: "35%" }}>
                            {k}
                          </td>
                          <td>{String(v)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <h6 className="fw-bold">Transcript</h6>
              {Array.isArray(selected?.transcript) && selected.transcript.length > 0 ? (
                <div className="border rounded p-3" style={{ maxHeight: "400px", overflowY: "auto" }}>
                  {selected.transcript.map((turn, idx) => (
                    <div key={idx} className="mb-2">
                      <span className={`badge ${turn.role === "bot" ? "bg-primary" : "bg-success"} me-2`}>
                        {turn.role === "bot" ? "Bot" : "Candidate"}
                      </span>
                      <span>{turn.text}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted">No transcript available.</p>
              )}
            </>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
}
